import { AlertTriangle, ArrowLeft, Eye, EyeOff, Loader2, Plus } from "lucide-react";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminModuli, useCreaModulo, useModificaModulo } from "@/hooks/useAdminModuli";
import { useAvanzamento } from "@/hooks/useAvanzamento";
import { useMe } from "@/hooks/useMe";
import { ApiError } from "@/lib/api";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";
import type { AdminModulo, ModuloStato, ModuloTipo } from "@/types/api";

const STATI: { value: ModuloStato; label: string }[] = [
  { value: "roadmap", label: "Roadmap" },
  { value: "anteprima", label: "Anteprima" },
  { value: "beta", label: "Beta" },
  { value: "live", label: "Live" },
  { value: "dismesso", label: "Dismesso" },
];

const TIPI: { value: ModuloTipo; label: string }[] = [
  { value: "modulo_funzionale", label: "Funzionale" },
  { value: "infrastruttura", label: "Infrastruttura" },
  { value: "agente_trasversale", label: "Trasversale" },
];

function tipoLabel(t: ModuloTipo): string {
  return TIPI.find((x) => x.value === t)?.label ?? t;
}

/** Gemella di `PERCENTUALE_PER_STATO` in `core/services/avanzamento.py`.
 *  Serve solo come placeholder dell'input: il valore vero lo calcola il backend. */
const PERCENTUALE_PER_STATO: Record<ModuloStato, number> = {
  roadmap: 0,
  anteprima: 33,
  beta: 66,
  live: 100,
  dismesso: 0,
};

const STATO_COLORI: Record<ModuloStato, string> = {
  roadmap: "bg-muted text-muted-foreground border-muted-foreground/20",
  anteprima: "bg-primary/10 text-primary border-primary/30",
  beta: "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800",
  live: "bg-success/10 text-success border-success/30",
  dismesso: "bg-muted/50 text-muted-foreground/70 border-muted-foreground/10",
};

export function AdminModuliPage() {
  const { data: me, isLoading: loadingMe } = useMe();
  const { data: moduli = [], isLoading } = useAdminModuli();

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Backoffice Maitime
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Stato moduli
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Promuovi i moduli lungo il loro ciclo di vita: <span className="font-medium">roadmap → anteprima → beta → live → dismesso</span>.
          La visibilità cliente controlla se il modulo compare nella lista lato Mister Pet (TopNav, sezione Moduli).
        </p>
        <p className="text-sm text-muted-foreground max-w-2xl">
          <span className="font-medium">Avanzamento</span>: la percentuale mostrata a
          Enzo nella casella «A che punto sono i moduli». Vuota = dedotta dallo stato
          (roadmap 0 · anteprima 33 · beta 66 · live 100). Scrivi un numero solo quando
          la scala mente — es. un modulo <em>live</em> con un pezzo ancora aperto.
          Conta solo per i moduli funzionali: sono gli unici nel totale di progetto.
        </p>
        <p className="text-sm text-muted-foreground max-w-2xl">
          <span className="font-medium">Go-live</span>: dal 21-08-2026 è{" "}
          <span className="font-medium">per modulo</span> — la data in cui è operativo
          al 100%. Il primo go-live futuro è quello che il cliente legge nel KPI
          «Prossima consegna». I deploy non riscrivono più queste date.
        </p>
        </div>
        <NuovoModulo />
      </header>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : moduli.length === 0 ? (
        <div className="rounded-lg border bg-card/40 p-6 text-center text-sm text-muted-foreground">
          Nessun modulo in DB. Esegui il seed iniziale.
        </div>
      ) : (
        <div className="rounded-lg border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground text-xs">
              <tr>
                <th className="text-left font-medium px-4 py-2 w-[80px]">Codice</th>
                <th className="text-left font-medium px-4 py-2">Nome</th>
                <th className="text-left font-medium px-4 py-2 w-[110px]">Tipo</th>
                <th className="text-left font-medium px-4 py-2 w-[160px]">Stato</th>
                <th className="text-left font-medium px-4 py-2 w-[140px]">
                  Visibilità cliente
                </th>
                <th className="text-left font-medium px-4 py-2 w-[150px]">
                  Avanzamento
                </th>
                <th className="text-left font-medium px-4 py-2 w-[150px]">Go-live</th>
                <th className="text-right font-medium px-4 py-2 w-[60px]">Ordine</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {moduli.map((m) => (
                <ModuloRow key={m.id} modulo={m} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageContainer>
  );
}

function ModuloRow({ modulo: m }: { modulo: AdminModulo }) {
  const modifica = useModificaModulo();

  function handleStato(e: React.ChangeEvent<HTMLSelectElement>) {
    const nuovo = e.target.value as ModuloStato;
    if (nuovo === m.stato) return;
    modifica.mutate({ codice: m.codice, body: { stato: nuovo } });
  }

  function handleVisibilita() {
    modifica.mutate({
      codice: m.codice,
      body: { is_visibile_cliente: !m.is_visibile_cliente },
    });
  }

  const isPending = modifica.isPending;

  return (
    <tr className={cn(isPending && "opacity-50")}>
      <td className="px-4 py-2.5">
        <Badge variant="outline" className="font-mono text-[11px]">
          {m.codice}
        </Badge>
      </td>
      <td className="px-4 py-2.5">
        <div className="font-medium text-foreground">{m.nome}</div>
        {m.sottotitolo && (
          <div className="text-xs text-muted-foreground mt-0.5">{m.sottotitolo}</div>
        )}
      </td>
      <td className="px-4 py-2.5 text-xs text-muted-foreground">
        {tipoLabel(m.tipo)}
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          <select
            value={m.stato}
            onChange={handleStato}
            disabled={isPending}
            className={cn(
              "rounded-md border px-2 py-1 text-xs font-medium",
              "focus:outline-none focus:ring-2 focus:ring-ring",
              "disabled:cursor-not-allowed",
              STATO_COLORI[m.stato]
            )}
          >
            {STATI.map((s) => (
              <option key={s.value} value={s.value} className="bg-background text-foreground">
                {s.label}
              </option>
            ))}
          </select>
          {isPending && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
        </div>
      </td>
      <td className="px-4 py-2.5">
        <button
          type="button"
          onClick={handleVisibilita}
          disabled={isPending}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors",
            "disabled:cursor-not-allowed disabled:opacity-50",
            m.is_visibile_cliente
              ? "bg-success/10 text-success border-success/30 hover:bg-success/20"
              : "bg-muted text-muted-foreground border-muted-foreground/20 hover:bg-muted/70"
          )}
          title={
            m.is_visibile_cliente
              ? "Visibile al cliente — click per nascondere"
              : "Nascosto al cliente — click per mostrare"
          }
        >
          {m.is_visibile_cliente ? (
            <>
              <Eye className="size-3.5" /> Visibile
            </>
          ) : (
            <>
              <EyeOff className="size-3.5" /> Nascosto
            </>
          )}
        </button>
      </td>
      <td className="px-4 py-2.5">
        <PercentualeCella modulo={m} />
      </td>
      <td className="px-4 py-2.5">
        <GoLiveCella modulo={m} />
      </td>
      <td className="px-4 py-2.5 text-right text-xs text-muted-foreground tabular-nums">
        {m.ordine}
      </td>
    </tr>
  );
}

/**
 * La percentuale mostrata a Enzo. Casella vuota = dedotta dallo stato.
 *
 * `null` è un valore, non un'assenza: svuotare il campo rimanda alla scala.
 * Per questo il PATCH manda `percentuale_override: null` esplicito invece di
 * omettere il campo.
 */
function PercentualeCella({ modulo: m }: { modulo: AdminModulo }) {
  const modifica = useModificaModulo();
  const [bozza, setBozza] = useState<string>(
    m.percentuale_override === null ? "" : String(m.percentuale_override)
  );

  const dedotta = PERCENTUALE_PER_STATO[m.stato];
  const funzionale = m.tipo === "modulo_funzionale";

  function salva() {
    const grezzo = bozza.trim();
    const valore = grezzo === "" ? null : Number(grezzo);
    if (valore !== null && (!Number.isInteger(valore) || valore < 0 || valore > 100)) {
      // Input non valido: torniamo al valore salvato invece di mandare spazzatura.
      setBozza(m.percentuale_override === null ? "" : String(m.percentuale_override));
      return;
    }
    if (valore === m.percentuale_override) return;
    modifica.mutate({ codice: m.codice, body: { percentuale_override: valore } });
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={0}
        max={100}
        value={bozza}
        placeholder={String(dedotta)}
        disabled={modifica.isPending || !funzionale}
        onChange={(e) => setBozza(e.target.value)}
        onBlur={salva}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        aria-label={`Avanzamento ${m.codice}`}
        className={cn(
          "w-16 rounded-md border px-2 py-1 text-xs tabular-nums",
          "focus:outline-none focus:ring-2 focus:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          m.percentuale_override === null && "text-muted-foreground"
        )}
        title={
          funzionale
            ? "Vuoto = dedotta dallo stato"
            : "Solo i moduli funzionali entrano nel totale di progetto"
        }
      />
      <span className="text-xs text-muted-foreground">%</span>
      {modifica.isPending && (
        <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}

/** La data di go-live: dal 21-08-2026 è per modulo, e alimenta «Prossima consegna». */
function GoLiveCella({ modulo: m }: { modulo: AdminModulo }) {
  const modifica = useModificaModulo();
  return (
    <div className="flex items-center gap-2">
      <input
        type="date"
        value={m.data_go_live?.slice(0, 10) ?? ""}
        disabled={modifica.isPending}
        onChange={(e) =>
          modifica.mutate({
            codice: m.codice,
            body: { data_go_live: e.target.value || null },
          })
        }
        aria-label={`Go-live ${m.codice}`}
        className={cn(
          "w-[135px] rounded-md border px-2 py-1 text-xs",
          "focus:outline-none focus:ring-2 focus:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          !m.data_go_live && "text-muted-foreground"
        )}
      />
      {modifica.isPending && (
        <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}

/**
 * «Nuovo modulo» — una **voce di vetrina**, non un modulo funzionante.
 *
 * Enrico, 21-08-2026: «dovranno essere solo vetrina, perché li dovremo
 * contrattualizzare, quindi diventerebbero un nuovo progetto». Il pannello lo
 * dice, perché è la prima cosa che si dimentica.
 *
 * E dice anche l'aritmetica: un modulo funzionale a 0% **abbassa** la
 * percentuale che Enzo vede in prima pagina. È corretto — più perimetro, meno
 * fatto — ma va saputo prima di premere, non scoperto guardando il KPI.
 */
function NuovoModulo() {
  const crea = useCreaModulo();
  const { data: moduli = [] } = useAdminModuli();
  const { data: avanzamento } = useAvanzamento();
  const [aperto, setAperto] = useState(false);
  const [codice, setCodice] = useState("");
  const [nome, setNome] = useState("");
  const [sottotitolo, setSottotitolo] = useState("");
  const [tipo, setTipo] = useState<ModuloTipo>("modulo_funzionale");
  const [goLive, setGoLive] = useState("");
  const [errore, setErrore] = useState<string | null>(null);

  const valido = /^[A-Z][A-Z0-9_]*$/.test(codice.trim()) && nome.trim() !== "";
  const prossimoOrdine = moduli.reduce((max, m) => Math.max(max, m.ordine), 0) + 1;

  // Che cosa succede al numero in prima pagina, detto prima di premere.
  const impatto =
    tipo === "modulo_funzionale" && avanzamento
      ? Math.round(
          (avanzamento.totale * avanzamento.n_moduli) / (avanzamento.n_moduli + 1)
        )
      : null;

  if (!aperto) {
    return (
      <Button variant="outline" onClick={() => setAperto(true)}>
        <Plus className="size-4" />
        Nuovo modulo
      </Button>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-5 space-y-3 w-full">
      <div className="space-y-1">
        <h2 className="text-sm font-medium">Nuovo modulo</h2>
        <p className="text-xs text-muted-foreground max-w-2xl">
          È una <span className="font-medium">voce di vetrina</span>: compare in roadmap
          per il cliente, ma non crea né il permesso di accesso né una pagina. Quelli
          sono sviluppo — e, come dici tu, un nuovo contratto.
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <label className="space-y-1">
          <span className="text-[11px] text-muted-foreground block">Codice *</span>
          <input
            type="text"
            value={codice}
            onChange={(e) => setCodice(e.target.value.toUpperCase())}
            placeholder="M6"
            aria-label="Codice del modulo"
            className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>
        <label className="space-y-1 md:col-span-2">
          <span className="text-[11px] text-muted-foreground block">Nome *</span>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Qualità e conformità"
            aria-label="Nome del modulo"
            className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[11px] text-muted-foreground block">Go-live</span>
          <input
            type="date"
            value={goLive}
            onChange={(e) => setGoLive(e.target.value)}
            aria-label="Go-live del modulo"
            className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>
        <label className="space-y-1 md:col-span-3">
          <span className="text-[11px] text-muted-foreground block">Sottotitolo</span>
          <input
            type="text"
            value={sottotitolo}
            onChange={(e) => setSottotitolo(e.target.value)}
            placeholder="Una riga che dice a cosa serve"
            aria-label="Sottotitolo"
            className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[11px] text-muted-foreground block">Tipo</span>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value as ModuloTipo)}
            aria-label="Tipo di modulo"
            className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {TIPI.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {impatto !== null && avanzamento && (
        <p className="text-xs text-warning inline-flex items-center gap-1.5">
          <AlertTriangle className="size-3.5 shrink-0" />
          Entrerà nella media: l'avanzamento che vede il cliente passa da{" "}
          <span className="font-medium">{avanzamento.totale}%</span> a{" "}
          <span className="font-medium">{impatto}%</span>.
        </p>
      )}

      {errore && (
        <p className="text-xs text-destructive inline-flex items-center gap-1.5">
          <AlertTriangle className="size-3.5 shrink-0" />
          {errore}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button
          disabled={!valido || crea.isPending}
          onClick={() => {
            setErrore(null);
            crea.mutate(
              {
                codice: codice.trim(),
                nome: nome.trim(),
                sottotitolo: sottotitolo.trim() || null,
                tipo,
                ordine: prossimoOrdine,
                data_go_live: goLive || null,
              },
              {
                onSuccess: () => {
                  setCodice("");
                  setNome("");
                  setSottotitolo("");
                  setGoLive("");
                  setAperto(false);
                },
                onError: (err) =>
                  setErrore(
                    err instanceof ApiError ? String(err.message) : "Creazione rifiutata."
                  ),
              }
            );
          }}
        >
          {crea.isPending && <Loader2 className="size-4 animate-spin" />}
          Crea
        </Button>
        <Button variant="ghost" onClick={() => setAperto(false)}>
          Annulla
        </Button>
      </div>
    </div>
  );
}
